import { useMemo, useRef } from 'react';
import type { OpenScenario } from '../../types';

export const SCENARIO_COLOR_PALETTE = [
  '#C084FC', // purple  (V1)
  '#FBBF24', // yellow  (V2)
  '#34D399', // emerald (V3)
  '#60A5FA', // blue    (V4)
  '#F87171', // red     (V5)
  '#FB923C', // orange  (V6)
  '#A78BFA', // violet  (V7)
  '#2DD4BF', // teal    (V8)
];

/**
 * Assigns a stable color to each scenario keyed by RequestID.
 * When a scenario is closed its palette slot is freed so new scenarios
 * can reuse it, preventing color collisions after 8+ total scenarios.
 */
export function useScenarioColors(scenarios: OpenScenario[]): ReadonlyMap<string, string> {
  const colorMapRef = useRef<Map<string, string>>(new Map());

  return useMemo(() => {
    const activeIds = new Set(scenarios.map(s => s.scenario.RequestID));

    // Remove entries for scenarios that are no longer open.
    for (const id of colorMapRef.current.keys()) {
      if (!activeIds.has(id)) {
        colorMapRef.current.delete(id);
      }
    }

    const usedColors = new Set(colorMapRef.current.values());

    for (const { scenario } of scenarios) {
      if (!colorMapRef.current.has(scenario.RequestID)) {
        // Pick the first palette color not currently in use.
        const color =
          SCENARIO_COLOR_PALETTE.find(c => !usedColors.has(c)) ??
          SCENARIO_COLOR_PALETTE[colorMapRef.current.size % SCENARIO_COLOR_PALETTE.length];
        colorMapRef.current.set(scenario.RequestID, color);
        usedColors.add(color);
      }
    }

    return colorMapRef.current;
  }, [scenarios]);
}
